"use client";

import { useUserMeQuery } from "@/hooks/useUserMeQuery";

/**
 * Mirror Mode (copytrading) is gated to a beta allowlist. The SINGLE source
 * of truth is the backend env `MIRROR_ALLOWED_USERNAMES` (Railway), evaluated
 * server-side and surfaced as `user.mirrorAllowed` on /api/users/me. The
 * frontend just reads that flag — no hardcoded list here (the old hardcoded
 * copy drifted: keypi033 was added to the env but not to the list, so the UI
 * hid Mirror for them despite backend access). 2026-06-10.
 *
 * Server-side enforcement also lives in `backend/src/api/routes/copytrading.ts`
 * (`isMirrorAllowed`, same helper) — setting `copyMode: 'mirror_percentage'`
 * via API gets a 403 for non-allowlisted users regardless of this gate.
 */
export interface MirrorAccess {
  hasMirrorAccess: boolean;
  /** True while `/users/me` is still resolving. UI should keep the card disabled in this window. */
  isLoading: boolean;
}

export function useMirrorAccess(): MirrorAccess {
  const { data, isLoading } = useUserMeQuery();

  // Local development escape hatch: in `next dev` always grant access so the
  // developer can interact with the gated UI without a real allowlisted Privy
  // session. NODE_ENV is compile-time stable → never ships to production. The
  // backend still enforces the gate via MIRROR_ALLOWED_USERNAMES.
  const isDev = process.env.NODE_ENV === "development";

  const hasMirrorAccess = isDev || (data as any)?.mirrorAllowed === true;
  return { hasMirrorAccess, isLoading: isLoading && !data && !isDev };
}
